//@flow
import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import Moment from "moment";

import { Translate } from "@quantum/i18n";

import {
  type Id,
  type MedidaSelecionada,
  type EdicaoMedida,
  type ErrosSelecao,
  ID_PERIODO_DATA_ESPECIFICA_INDICADORES_MERCADO,
  FORMATO_DATA_BANCO,
} from "../../../../../model";
import {
  type State,
  type ActionCreator,
  type ProcessadorPeriodo,
  i18n,
  getPeriodoUnicoSelecionado,
  getDataEspecificaSelecionada,
  selecionarDataEspecifica,
  selecionarDataEspecificaDefault,
  getDataBaseDefault,
  getDataBaseMaxima,
  getEdicaoMedida,
  getDiasUteis,
  getIdiomaUsuario,
} from "../../../../../state";

import { DateCalendarPicker } from "../../../../comum";

type Props = {
  exibir: boolean,
  dataEspecificaSelecionada: ?Date,
  selecionarDataEspecifica: ActionCreator,
  selecionarDataEspecificaDefault: ActionCreator,
  dataBaseDefault: string,
  dataBaseMaxima: string,
  idiomaUsuario: string,
};

const exibirSelecaoDataEspecifica: (state: State) => boolean = (state: State) =>
  getPeriodoUnicoSelecionado(state) ===
  ID_PERIODO_DATA_ESPECIFICA_INDICADORES_MERCADO;

function SelecaoDataEspecifica({
  exibir,
  dataEspecificaSelecionada,
  selecionarDataEspecifica,
  selecionarDataEspecificaDefault,
  dataBaseDefault,
  dataBaseMaxima,
  idiomaUsuario,
}: Props) {
  useEffect(() => {
    if (!dataEspecificaSelecionada && exibir) {
      selecionarDataEspecificaDefault();
    }
  }, [dataEspecificaSelecionada, exibir, selecionarDataEspecificaDefault]);

  const validaData = useCallback(
    dataASerValidada => {
      const data = Moment(dataASerValidada, "DD/MM/YYYY", true);
      return (
        data.isSameOrAfter(Moment("19970101", FORMATO_DATA_BANCO)) &&
        data.isSameOrBefore(Moment(dataBaseMaxima, FORMATO_DATA_BANCO))
      );
    },
    [dataBaseMaxima]
  );

  const onDataChange = function(data) {
    selecionarDataEspecifica(data);
  };

  if (!exibir) {
    return null;
  }

  return (
    <div className="ConfiguracaoMedida">
      <label className="ConfiguracaoMedida__label">
        <Translate>maiusculas.data</Translate>
      </label>
      <DateCalendarPicker
        className="ConfiguracaoMedida__input-data"
        label={""}
        value={dataEspecificaSelecionada}
        onChange={onDataChange}
        closeOnSelect={true}
        strictParsing={true}
        isValidDate={validaData}
        locale={idiomaUsuario}
      />
    </div>
  );
}

export const validacaoSelecaoDataEspecifica = function(
  state: State
): ?ErrosSelecao {
  const diasUteis: Array<string> = getDiasUteis(state);
  const dataEspecificaSelecionada: ?Moment = getDataEspecificaSelecionada(
    state
  );
  const { translate } = i18n(state);

  if (exibirSelecaoDataEspecifica(state)) {
    if (!Moment.isMoment(dataEspecificaSelecionada)) {
      return {
        selecaoDataEspecifica: translate(
          "configuracaoRelatorio.erroDataEspecificaInvalida"
        ),
      };
    } else if (
      dataEspecificaSelecionada &&
      !diasUteis.includes(dataEspecificaSelecionada.format(FORMATO_DATA_BANCO))
    ) {
      return {
        selecaoDataEspecifica: translate(
          "configuracaoRelatorio.erroDataEspecificaDiaUtil"
        ),
      };
    }
  }

  return null;
};

export const processarPeriodoDataEspecifica: ProcessadorPeriodo = (
  periodo: Id,
  state: State,
  medidasSelecionadas: Array<MedidaSelecionada>
) => {
  const edicaoMedida: EdicaoMedida = getEdicaoMedida(state);
  const _medidasSelecionadas = [];

  medidasSelecionadas.forEach(medidaSelecionada => {
    _medidasSelecionadas.push({
      ...medidaSelecionada,
      periodo,
      dataEspecifica: edicaoMedida.dataEspecifica
        ? edicaoMedida.dataEspecifica.valueOf()
        : null,
    });
  });

  return _medidasSelecionadas;
};

export default connect(
  (state: State) => ({
    exibir: exibirSelecaoDataEspecifica(state),
    dataEspecificaSelecionada: getDataEspecificaSelecionada(state),
    dataBaseDefault: getDataBaseDefault(state),
    dataBaseMaxima: getDataBaseMaxima(state),
    idiomaUsuario: getIdiomaUsuario(state),
  }),
  {
    selecionarDataEspecifica,
    selecionarDataEspecificaDefault,
  }
)(SelecaoDataEspecifica);
